import { createSelector } from 'reselect'
import { emptyArr } from 'util/arrays'
import { emptyObj } from 'util/objects'
import { template } from 'util/strings'
import {
  currentPathSelector,
  selectAccountSubdomain,
  selectMailboxIds,
} from 'selectors/app'
import { selectCurrentPayload, selectCurrentPage } from 'selectors/location'
import { selectPrimaryKnowledgeBase } from 'subapps/kb/selectors/knowledge_bases'
import {
  SETTINGS_WIDGET_CONTACT_PAGE,
  SETTINGS_WIDGET_KB_PAGE,
  SETTINGS_WIDGET_CUSTOMIZE_PAGE,
  SETTINGS_WIDGET_INSTALL_PAGE,
  SETTINGS_WIDGET_FIRST_INSTALL_PAGE,
  SETTINGS_WIDGET_CHAT_PAGE,
} from 'constants/pages'

function selectBase(state) {
  return state.widgets || emptyObj
}

// FIXME: Remove once new wiget is out in the wild
export const selectWidgetsIsEnabled = createSelector(
  selectAccountSubdomain,
  subdomain => subdomain === 'groove'
)

export const selectWidgetsIsBootstrapped = createSelector(
  selectBase,
  base => !!base.isBootstrapped
)

export const selectWidgetsIsOnPage = createSelector(
  currentPathSelector,
  path => {
    if (!path) return false
    if (path.match(/\/settings\/integrations\/widgets/)) {
      return true
    }
    return false
  }
)

export const selectWidgetsIds = createSelector(
  selectBase,
  base => base.ids || emptyArr
)

export const selectWidgetsById = createSelector(
  selectBase,
  base => base.byId || emptyObj
)

export const selectWidgets = createSelector(
  selectWidgetsById,
  selectWidgetsIds,
  (byId, ids) => ids.map(id => byId[id])
)

export const selectWidgetsIsLoading = createSelector(
  selectBase,
  base => base.isLoading || !base.isBootstrapped
)

export const selectWidgetsIsSaving = createSelector(
  selectBase,
  base => base.isSaving
)

export const selectCurrentWidget = createSelector(
  selectCurrentPayload,
  selectWidgetsById,
  (payload, widgetsById) => widgetsById[payload.id] || emptyObj
)

export const selectCurrentWidgetId = createSelector(
  selectCurrentPayload,
  payload => payload.id
)

export const selectSettings = createSelector(
  selectBase,
  base => base.settings || emptyObj
)

export const selectCurrentWidgetSettings = createSelector(
  selectSettings,
  selectCurrentWidgetId,
  (settings, id) => settings[id] || emptyObj
)

export const selectCurrentWidgetSettingsIsPublished = createSelector(
  selectCurrentWidgetSettings,
  settings => !!settings.published
)

export const selectSnippet = createSelector(selectBase, base => base.snippet)

export const selectSnippetForInstallation = createSelector(
  selectSnippet,
  selectCurrentWidget,
  (snippet, widget) => {
    const { uuid } = widget
    return template(/%{{(\w+)}}%/g, snippet, {
      WIDGET_UUID: uuid,
      WIDGET_OPTIONS: {},
    })
  }
)

export const selectWidgetErrors = createSelector(
  selectBase,
  base => base.error || emptyObj
)

export const selectHidePreviewMessage = createSelector(
  selectBase,
  base => base.hidePreviewMessage || false
)

export const selectDemoSettings = createSelector(
  selectBase,
  selectMailboxIds,
  selectPrimaryKnowledgeBase,
  (base, mailboxIds, primaryKb) => {
    const final = { ...base.demoSettings }
    if (
      final['contact.enabled'] === undefined &&
      mailboxIds &&
      mailboxIds.length > 0
    ) {
      final['contact.enabled'] = true
      final['contact.id'] = mailboxIds[0]
    }
    if (final['kb.enabled'] === undefined && primaryKb) {
      final['kb.enabled'] = true
      final['kb.id'] = primaryKb.id
    }
    return final
  }
)

export const selectDemoUuid = createSelector(
  selectAccountSubdomain,
  subdomain => (subdomain ? `demo-${subdomain}` : null)
)

export const selectTopbarTitle = createSelector(
  selectCurrentPage,
  currentPage => {
    switch (currentPage) {
      case SETTINGS_WIDGET_CONTACT_PAGE:
        return 'Contact'
      case SETTINGS_WIDGET_KB_PAGE:
        return 'Knowledge base'
      case SETTINGS_WIDGET_CUSTOMIZE_PAGE:
        return 'Customize'
      case SETTINGS_WIDGET_CHAT_PAGE:
        return 'Chat'
      case SETTINGS_WIDGET_INSTALL_PAGE:
      case SETTINGS_WIDGET_FIRST_INSTALL_PAGE:
        return 'Install'
      default:
        return 'Widget'
    }
  }
)
